import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Clock, Mail, Lock, User, Building2, AlertCircle, CheckCircle2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const { language, toggleLanguage } = useLanguage();
  const isEs = language === 'es';
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(searchParams.get('plan') || '');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [appName, setAppName] = useState('Regulisto');

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [settingsRes, plansRes] = await Promise.all([
          axios.get(`${API_URL}/settings/public`),
          axios.get(`${API_URL}/plans/public`)
        ]);
        if (settingsRes.data?.app_name) setAppName(settingsRes.data.app_name);
        setPlans(plansRes.data || []);
        if (!searchParams.get('plan') && plansRes.data?.length) {
          setPlanId(plansRes.data[0].id);
        }
      } catch (err) {
        console.error('Error loading signup data:', err);
      }
    };
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedTerms) {
      setError(isEs ? 'Debes aceptar los Términos de Servicio y la Política de Privacidad' : 'You must accept the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/signup`, {
        company_name: companyName,
        admin_name: adminName,
        admin_email: adminEmail,
        admin_password: adminPassword,
        plan_id: planId,
        accepted_terms: acceptedTerms
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || (isEs ? 'Error al crear la cuenta' : 'Error creating account'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md shadow-lg border-slate-200 text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-xl">
              {isEs ? 'Revisa tu email' : 'Check your email'}
            </CardTitle>
            <CardDescription>
              {isEs
                ? `Te hemos enviado un enlace de confirmación a ${adminEmail}. Haz clic en él para activar tu cuenta y empezar tu prueba gratuita.`
                : `We sent a confirmation link to ${adminEmail}. Click it to activate your account and start your free trial.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/guide">
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="signup-guide-link">
                {isEs ? 'Mientras tanto, mira la guía de configuración' : 'Meanwhile, check out the setup guide'}
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="w-full">
                {isEs ? 'Volver al inicio de sesión' : 'Back to login'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50" data-testid="signup-page">
      <div className="absolute top-4 right-4">
        <Button variant="ghost" onClick={toggleLanguage} className="text-slate-600" data-testid="signup-language-toggle">
          {isEs ? '🇬🇧 EN' : '🇪🇸 ES'}
        </Button>
      </div>

      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-['Manrope'] text-slate-900">{appName}</span>
          </div>
          <CardTitle className="text-2xl font-['Manrope'] text-slate-900">
            {isEs ? 'Crea tu empresa' : 'Create your company'}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {isEs ? 'Empieza gratis, sin tarjeta de crédito' : 'Start free, no credit card required'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-testid="signup-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-slate-700">
                {isEs ? 'Nombre de la empresa' : 'Company name'}
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="companyName"
                  type="text"
                  placeholder={isEs ? 'Mi Empresa S.L.' : 'My Company Ltd.'}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="signup-company-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminName" className="text-slate-700">
                {isEs ? 'Tu nombre' : 'Your name'}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="adminName"
                  type="text"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="signup-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminEmail" className="text-slate-700">
                {isEs ? 'Email' : 'Email'}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="adminEmail"
                  type="email"
                  placeholder="nombre@empresa.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="signup-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminPassword" className="text-slate-700">
                {isEs ? 'Contraseña' : 'Password'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="adminPassword"
                  type="password"
                  placeholder="••••••••"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="signup-password-input"
                />
              </div>
            </div>

            {plans.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-700">{isEs ? 'Plan' : 'Plan'}</Label>
                <div className="grid grid-cols-1 gap-2">
                  {plans.map((plan) => (
                    <button
                      type="button"
                      key={plan.id}
                      onClick={() => setPlanId(plan.id)}
                      className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                        planId === plan.id
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`signup-plan-${plan.name}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{plan.display_name}</span>
                        <span className="text-sm text-slate-500">€{plan.price_monthly}/{isEs ? 'mes' : 'mo'}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Checkbox
                id="acceptedTerms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                className="mt-0.5"
                data-testid="signup-accept-terms"
              />
              <Label htmlFor="acceptedTerms" className="text-sm font-normal text-slate-600 leading-snug">
                {isEs ? 'He leído y acepto los ' : 'I have read and accept the '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 underline">
                  {isEs ? 'Términos de Servicio' : 'Terms of Service'}
                </Link>
                {isEs ? ' y la ' : ' and '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 underline">
                  {isEs ? 'Política de Privacidad' : 'Privacy Policy'}
                </Link>
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={loading || !planId || !acceptedTerms}
              data-testid="signup-submit"
            >
              {loading
                ? (isEs ? 'Creando cuenta...' : 'Creating account...')
                : (isEs ? 'Empieza gratis' : 'Start free trial')}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            {isEs ? '¿Ya tienes cuenta?' : 'Already have an account?'}{' '}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="signup-login-link">
              {isEs ? 'Iniciar sesión' : 'Sign in'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUp;
